import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Select, EmptyState } from "../components/ui-kit";
import { Download, Upload, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { fmtDateIST } from "../lib/datefmt";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function BRWeeklyPage() {
  const { user } = useAuth();
  const canUpload = ["admin", "manager", "director"].includes(user?.role);
  const [data, setData] = useState({ rows: [], total_outstanding: 0, total_over_3m: 0 });
  const [q, setQ] = useState("");
  const [salesPerson, setSalesPerson] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => setData((await api.get("/dealer-outstanding")).data);  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const salesPeople = useMemo(() => {
    const m = new Map();
    data.rows.forEach((r) => { if (r.sales_person_id) m.set(r.sales_person_id, r.sales_person_name || "Unnamed"); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data.rows]);

  const filtered = useMemo(
    () =>
      data.rows.filter((r) =>
        (q ? [r.customer_name, r.customer_code, r.sales_person_name].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())) : true) &&
        (salesPerson ? (salesPerson === "__none__" ? !r.sales_person_id : r.sales_person_id === salesPerson) : true)
      ),
    [data.rows, q, salesPerson]
  );

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/dealer-outstanding/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "dealer_outstanding_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: result } = await api.post("/dealer-outstanding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${result.updated} dealer balance${result.updated === 1 ? "" : "s"} updated`);
      if (result.errors?.length) {
        toast.warning(`${result.errors.length} row(s) skipped — see console`);
        logger.warn("BR upload errors:", result.errors);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="BR — Bills Receivable"
        subtitle="Current outstanding + over 3-month overdue for every dealer"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="br-download">
              <Download className="w-4 h-4" /> Download Excel
            </Button>
            {canUpload && (
              <label className={`inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20] text-white hover:bg-[#153217] rounded-lg px-4 py-2 font-semibold text-sm ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload balances"}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="br-upload"
                  onChange={(e) => onUpload(e.target.files?.[0])}
                />
              </label>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-3">
            <IndianRupee className="w-5 h-5 text-[#1E3F20]" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Total outstanding</div>
              <div className="text-3xl font-heading font-black text-[#1E3F20]" data-testid="total-outstanding">{money(data.total_outstanding)}</div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Over 3 months overdue</div>
          <div className="text-3xl font-heading font-black text-red-700" data-testid="total-over3m">{money(data.total_over_3m)}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E] font-bold">Dealers with balance</div>
          <div className="text-3xl font-heading font-black text-[#1E3F20]" data-testid="dealers-with-balance">
            {data.rows.filter((r) => Number(r.amount) > 0).length}
          </div>
        </Card>
        <Card>
          <div className="space-y-2">
            <Input placeholder="Search dealer, code or sales person…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="br-search" />
            <Select value={salesPerson} onChange={(e) => setSalesPerson(e.target.value)} data-testid="br-sales-person-filter">
              <option value="">All sales persons</option>
              {salesPeople.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
              <option value="__none__">Unassigned</option>
            </Select>
          </div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No dealers" description="Add customers first, then upload their balances." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Dealer</th>
                  <th className="px-4 py-3">Sales person</th>
                  <th className="px-4 py-3 text-right">Outstanding</th>
                  <th className="px-4 py-3 text-right">Over 3M Overdue</th>
                  <th className="px-4 py-3">Remarks</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.customer_id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`br-row-${r.customer_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.customer_code || "—"}</td>
                    <td className="px-4 py-3 font-semibold">{r.customer_name}</td>
                    <td className="px-4 py-3 text-[#1A2E1C]" data-testid={`br-sales-person-${r.customer_id}`}>
                      {r.sales_person_name || <span className="text-[#8A8577]">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-[#1E3F20]">{money(r.amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-700" data-testid={`br-over3m-${r.customer_id}`}>
                      {Number(r.over_3m_amount) > 0 ? money(r.over_3m_amount) : <span className="text-[#8A8577]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{r.remarks}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6B5E]">
                      {r.updated_at ? fmtDateIST(r.updated_at) : "—"}
                      {r.updated_by_name ? <div>by {r.updated_by_name}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
